import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import CountryPropType from '../../../propTypes/Country.proptype';
import FieldContainerPropType from '../../../propTypes/FieldContainer.proptype';
import NewAddressPropType from '../../../propTypes/NewAddress.proptype';
import SelectableContainer from '../../../UI/SelectableContainer/SelectableContainer';
import {
  updateDeliveryAddress,
  updateDeliveryAddressField,
} from '../../actions/address-actions';
import selectAddressActions from '../../actions/addressActions/selectAddressActions';
import selectCountryActions from '../../actions/addressActions/selectCountryActions';
import {getDeliveryOptions, resetDeliveryOptions} from '../../actions/deliveryOptionActions';
import {inEditDeleteExperiment} from '../../selectors/ExperimentSelectors';
import getShouldShowPudoNotAvailable from '../../selectors/getShouldShowPudoNotAvailable';
import {
  getSelectedAddressCategory,
  getSelectedAddressID,
  getSettings,
  isPudoAvailable,
} from '../../selectors/selectors';
import AddNewAddress from './AddNewAddress/AddNewAddress';
import addressDefinitionsByCountry from './address-definitions-by-country';
import AddressContainer from './AddressContainer';
import AddressRadioButton from './AddressRadioButton';

export function DeliveryAddressList(props) {
  const selectAddress = (address) => {
    props.selectAddressActions(address.addressId, address.selectedAddressCategory, props.newAddress.country);
  };
  
  const countrySetter = addressId => (event) => {
    props.selectCountryActions(addressId, event.target.value);
  };
  
  const newAddress = () => {
    let customClass = '';
    let disabled = false;
    if (props.newAddress.addressId !== props.addressId) {
      customClass = 'hidden';
      disabled = true;
    }
    
    return (
      <AddressContainer
        disabled={disabled}
        className={`new-delivery-address-fields ${customClass}`}
        addressType="delivery"
        resetAddress={(newValues, addressId = '') => props.updateDeliveryAddress(addressId, newValues)}
        countries={props.countries}
        onCountryChange={countrySetter(props.newAddress.addressId)}
        fieldValues={props.newAddress}
        definedFields={addressDefinitionsByCountry.get(props.newAddress.country)}
        isIncludeExtraFields
        addressId={props.newAddress.addressId}
        selectedAddressCategory={props.newAddress.selectedAddressCategory}
        fieldContainer={props.fieldContainer.fieldUpdateDispatcher(
          (fieldName, value) => props.updateDeliveryAddressField(
            {addressId: props.newAddress.addressId}, fieldName, value,
          ),
        )}
        fieldContainerPhone={props.fieldContainer}
        updateDeliveryOptions={props.getDeliveryOptions}
        onTaxCountyChange={props.getDeliveryOptions}
        inEditDeleteExperiment={props.inEditDeleteExperiment}
        onClick={selectAddress}
        selectedAddressId={props.selectedAddressId}
        hideCountryRegionDropdown={props.hideCountryRegionDropdown}
      />
    );
  };
  
  const newAddressButton = () => {
    const isSelected = props.newAddress.addressId === props.addressId;
    return (
      <div className="field" data-test="DeliveryAddressList__newAddress" key={props.newAddress.addressId}>
        <SelectableContainer
          componentId="newDeliveryAddress"
          isSelected={isSelected}
        >
          <AddressRadioButton
            hidden
            onChange={selectAddress}
            disabled={props.disabled}
            id="add-new-address"
            addressId={props.newAddress.addressId}
            checkAgainst={props.addressId}
            country={props.newAddress.country}
            county={props.newAddress.county}
            selectedAddressCategory={props.newAddress.selectedAddressCategory}
          >
            <AddNewAddress isSelected={isSelected} />
          </AddressRadioButton>
          {isSelected && (
            <div className="DeliveryAddressList__newAddressForm">
              {newAddress()}
            </div>
          )}
        </SelectableContainer>
      </div>
    );
  };
  
  const savedAddresses = () => {
    const items = props.savedAddresses.map(address => (
      <div className={`stored-address deliveryAddress_storedAddress ${!props.isPudoAvailable && props.shouldShowPudoNotAvailable && 'addMessageStoredAddress'}`} key={address.addressId}>
        <SelectableContainer
          componentId="storedDeliveryAddress"
          isSelected={address.addressId === props.addressId}
        >
          <AddressRadioButton
            hidden
            disabled={props.disabled}
            onChange={selectAddress}
            id={`AddressRadioButton-${address.addressId}`}
            addressId={address.addressId}
            checkAgainst={props.addressId}
            country={address.country}
            county={address.county}
            selectedAddressCategory={address.selectedAddressCategory}
          >
            <AddressContainer
              addressType="delivery"
              countries={props.countries}
              onCountryChange={countrySetter(address.addressId)}
              fieldValues={address}
              definedFields={addressDefinitionsByCountry.get(address.country)}
              isIncludeExtraFields={props.addressId === address.addressId}
              addressId={address.addressId}
              selectedAddressCategory={address.selectedAddressCategory}
              inEditDeleteExperiment={props.inEditDeleteExperiment}
              onClick={selectAddress}
              selectedAddressId={props.selectedAddressId}
              fieldContainer={props.fieldContainer.fieldUpdateDispatcher(
                (fieldName, value) => props.updateDeliveryAddressField(
                  {addressId: address.addressId}, fieldName, value,
                ),
              )}
              hideCountryRegionDropdown={props.hideCountryRegionDropdown}
              defaultDeliveryAddress={address.defaultDeliveryAddress}
              defaultBillingAddress={address.defaultBillingAddress}
            />
          </AddressRadioButton>
        </SelectableContainer>
      </div>
    ));
    
    return (
      <div className="fields" id="stored-addresses">
        {items}
        {(props.savedAddresses.length !== 0) && newAddressButton()}
      </div>
    );
  };
  
  if (props.disabled) return null;
  
  return (
    <div id="delivery-address-fields">
      {props.savedAddresses.length > 0 && savedAddresses()}
      {props.savedAddresses.length === 0 && newAddress()}
    </div>
  );
}

DeliveryAddressList.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  savedAddresses: PropTypes.array.isRequired,
  newAddress: PropTypes.shape(NewAddressPropType),
  getDeliveryOptions: PropTypes.func.isRequired,
  resetDeliveryOptions: PropTypes.func.isRequired,
  updateDeliveryAddress: PropTypes.func.isRequired,
  selectCountryActions: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryPropType)),
  addressId: PropTypes.string.isRequired,
  selectedAddressId: PropTypes.string.isRequired,
  selectedAddressCategory: PropTypes.string.isRequired,
  selectAddress: PropTypes.func.isRequired,
  selectAddressActions: PropTypes.func.isRequired,
  updateDeliveryAddressField: PropTypes.func.isRequired,
  fieldContainer: PropTypes.shape(FieldContainerPropType).isRequired,
  disabled: PropTypes.bool.isRequired,
  shouldShowImportWarning: PropTypes.bool.isRequired,
  inEditDeleteExperiment: PropTypes.bool,
  isMobile: PropTypes.bool,
  hideCountryRegionDropdown: PropTypes.bool.isRequired,
  isPudoAvailable: PropTypes.bool.isRequired,
  shouldShowPudoNotAvailable: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  countries: state.address.deliveryAddressList.countries,
  addressId: state.address.deliveryAddressList.addressId,
  savedAddresses: state.address.deliveryAddressList.savedAddressIds.map(addressId => (
    state.address.deliveryAddressList.addresses[addressId])),
  newAddress: state.address.deliveryAddressList.addresses[''],
  shouldShowImportWarning: !!getSettings(state)?.show_import_warning,
  inEditDeleteExperiment: inEditDeleteExperiment(state),
  selectedAddressId: getSelectedAddressID(state),
  selectedAddressCategory: getSelectedAddressCategory(state),
  hideCountryRegionDropdown: getSettings(state)?.hide_country_region_dropdown_delivery,
  isPudoAvailable: isPudoAvailable(state),
  shouldShowPudoNotAvailable: getShouldShowPudoNotAvailable(state),
});

const mapDispatchToProps = {
  getDeliveryOptions,
  resetDeliveryOptions,
  updateDeliveryAddress,
  updateDeliveryAddressField,
  selectAddressActions,
  selectCountryActions,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DeliveryAddressList);
